import React, { useMemo, useState, useEffect } from "react";
import HeaderNormal from "../components/HeaderNormal";
import Footer from "../components/Footer";
import "../styles/Realisations.css";

import RealisationCard from "../components/realisations/RealisationCards";
import RealisationEditor from "../components/realisations/RealisationEditor";
import RealisationModal from "../components/realisations/RealisationModal";

import { apiFetch, logout as authLogout } from "../api/auth";
import { getRealisations, createRealisation, updateRealisation, deleteRealisation } from "../api/realisations";

const FIXED_CATEGORIES = [
  { key: "CONSEIL_FORMATION", label: "CONSEIL ET FORMATION", description: "Préparation technique, formation navigation..." },
  { key: "PREPA_GRANDE_CROISIERE", label: "PRÉPARATION GRANDE CROISIÈRE", description: "Transformation, refit aluminium, composite..." },
  { key: "COQUE_PONT", label: "PRÉPARATION COQUE & PONT", description: "META-Grip, enduits MAP Yachting..." },
  { key: "METALLERIE", label: "TRAVAUX DE MÉTALLERIE", description: "Soudure alu/inox, portiques, casquettes..." },
  { key: "GREEMENT_VOILES", label: "GRÉEMENT & VOILES", description: "Dormant, courant, textile, voiles..." },
  { key: "NETTOYAGE_CONCIERGERIE", label: "NETTOYAGE & CONCIERGERIE", description: "" },
];

export default function Realisations() {
  const [items, setItems] = useState([]);

  const [q, setQ] = useState("");
  const [cat, setCat] = useState("Toutes");
  const [sort, setSort] = useState("year-desc");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 12;

  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [editingId, setEditingId] = useState(null); // null | "NEW" | _id
  const [draft, setDraft] = useState(null);

  const [activeItem, setActiveItem] = useState(null); // ✅ modal

  useEffect(() => {
    (async () => {
      try {
        const meRes = await apiFetch("/api/auth/me");
        const me = await meRes.json();
        setIsAdmin(Boolean(me?.isAuth && me?.user?.role === "admin"));

        const list = await getRealisations();
        setItems(list);
      } catch (e) {
        console.error(e);
        setItems([]);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const CATEGORIES = useMemo(() => {
    return ["Toutes", ...FIXED_CATEGORIES.map((c) => c.key)];
  }, []);

  const filtered = useMemo(() => {
    const byCat =
      cat === "Toutes"
        ? items
        : items.filter((r) => Array.isArray(r.categories) && r.categories.includes(cat));

    const byQ = q.trim()
      ? byCat.filter((r) =>
          [r.title, r.description, (r.tags || []).join(" "), (r.categories || []).join(" ")]
            .join(" ")
            .toLowerCase()
            .includes(q.toLowerCase())
        )
      : byCat;

    const sorted = [...byQ].sort((a, b) => {
      switch (sort) {
        case "year-asc":
          return (a.year ?? 0) - (b.year ?? 0);
        case "title-asc":
          return (a.title || "").localeCompare(b.title || "");
        case "title-desc":
          return (b.title || "").localeCompare(a.title || "");
        case "year-desc":
        default:
          return (b.year ?? 0) - (a.year ?? 0);
      }
    });

    return sorted;
  }, [items, q, cat, sort]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const current = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [q, cat, sort]);

  // ✅ Au clic sur la card -> modal
  const handleOpen = (item) => {
    setActiveItem(item);
  };

  function startCreate() {
    setEditingId("NEW");
    setDraft({
      title: "",
      description: "",
      categories: [],
      tags: [],
      year: new Date().getFullYear(),
      link: "",
      coverImage: { url: "", alt: "", publicId: "" },
      images: [],
    });
  }

  function startEdit(item) {
    const clone = JSON.parse(JSON.stringify(item));
    if (!clone.categories) clone.categories = clone.category ? [clone.category] : [];
    delete clone.category;

    setEditingId(item._id);
    setDraft(clone);
  }

  function cancelEdit() {
    setEditingId(null);
    setDraft(null);
  }

  async function onDelete(item) {
    if (!window.confirm("Supprimer cette réalisation ?")) return;
    setSaving(true);
    setErrorMsg("");
    try {
      await deleteRealisation(item._id);
      setItems((prev) => prev.filter((x) => x._id !== item._id));
      cancelEdit();
    } catch (e) {
      setErrorMsg(e?.message || "Erreur suppression");
    } finally {
      setSaving(false);
    }
  }

  async function saveDraft(payload) {
    const data = payload ?? draft;
    if (!data) return;

    if (!data.title || data.title.trim().length < 2) {
      alert("Titre invalide");
      return;
    }
    if (!Array.isArray(data.categories) || data.categories.length === 0) {
      alert("Choisis au moins une catégorie.");
      return;
    }

    setSaving(true);
    setErrorMsg("");
    try {
      if (editingId === "NEW") {
        const created = await createRealisation(data);
        setItems((prev) => [created, ...prev]);
      } else {
        const updated = await updateRealisation(editingId, data);
        setItems((prev) => prev.map((x) => (x._id === editingId ? updated : x)));
      }
      cancelEdit();
    } catch (e) {
      setErrorMsg(e?.message || "Erreur sauvegarde");
    } finally {
      setSaving(false);
    }
  }

  function handleLogout() {
    authLogout();
    setIsAdmin(false);
    cancelEdit();
  }

  return (
    <>
      <HeaderNormal />

      <h1>
        Nos <span>Réalisations</span>
      </h1>

      <section className="realisations container">
        <header className="realisations__controls">
          <input
            className="real-input"
            placeholder="Rechercher (titre, tags, catégories...)"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />

          <select className="real-select" value={cat} onChange={(e) => setCat(e.target.value)}>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c === "Toutes" ? "Toutes" : c}
              </option>
            ))}
          </select>

          <select className="real-select" value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="year-desc">Plus récent → ancien</option>
            <option value="year-asc">Ancien → récent</option>
            <option value="title-asc">Titre A → Z</option>
            <option value="title-desc">Titre Z → A</option>
          </select>

          {isAdmin && (
            <div className="admin-scope">
              <div className="real-adminbar">
                <button className="real-btn" type="button" onClick={startCreate} disabled={saving}>
                  + Ajouter
                </button>
                <button className="real-btn" type="button" onClick={handleLogout} disabled={saving}>
                  Déconnexion
                </button>
              </div>

              {(editingId === "NEW" || editingId) && draft && (
                <RealisationEditor
                  editingId={editingId}
                  draft={draft}
                  setDraft={setDraft}
                  onSave={saveDraft}
                  onCancel={cancelEdit}
                  saving={saving}
                  categories={FIXED_CATEGORIES}
                />
              )}
            </div>
          )}
        </header>

        {errorMsg && <p className="real-empty">{errorMsg}</p>}
        {loading && <p className="real-empty">Chargement…</p>}

        <div className="realisations__grid">
          {!loading &&
            current.map((item) => (
              <RealisationCard
                key={item._id}
                item={item}
                onClick={handleOpen}
                isAdmin={isAdmin}
                onEdit={() => startEdit(item)}
                onDelete={() => onDelete(item)}
                disabled={saving}
              />
            ))}

          {!loading && current.length === 0 && (
            <p className="real-empty">Aucune réalisation ne correspond à votre recherche.</p>
          )}
        </div>

        {totalPages > 1 && (
          <nav className="real-pagination">
            <button className="real-btn" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              ← Précédent
            </button>

            <span className="real-pageinfo">
              Page {page} / {totalPages} • {total} résultat{total > 1 ? "s" : ""}
            </span>

            <button
              className="real-btn"
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Suivant →
            </button>
          </nav>
        )}
      </section>

      <Footer />

      {/* ✅ MODAL */}
      {activeItem && <RealisationModal item={activeItem} onClose={() => setActiveItem(null)} />}
    </>
  );
}
